import { useEffect, useState } from "react";
import API from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import {
  Mail, MessageSquare, Bell, Webhook, CreditCard, Loader2, FlaskConical,
  CheckCircle2, Clock, Ban,
} from "lucide-react";

/**
 * Per-order "What would have fired" drawer. Opens from the Sandbox tab
 * when a prescriber (or admin) clicks "Events" on a seeded order.
 *
 * Shows two sections:
 *   1. Lifecycle preview — every outbound hook the live production flow
 *      would trigger for this scenario (approval email, LifeFile push,
 *      Intuit charge, SMS, delivery notice, denial email). Each tile is
 *      marked `fired` / `pending` / `skipped` based on the order status
 *      and any audit rows in `sandbox_events`.
 *   2. Recorded events — the exact `sandbox_events` audit rows written
 *      by the stubbed hooks for this order (if any).
 *
 * Everything is sandbox data — the patient_email is a synthetic
 * `@sandbox.glpflo.test` address, no real patient PII is exposed.
 */
const KIND_META = {
  email: { Icon: Mail, color: "text-blue-500", label: "Email" },
  sms: { Icon: MessageSquare, color: "text-cyan-500", label: "SMS" },
  push: { Icon: Bell, color: "text-fuchsia-500", label: "Push" },
  lifefile_push: { Icon: Webhook, color: "text-emerald-500", label: "LifeFile" },
  intuit_charge: { Icon: CreditCard, color: "text-amber-500", label: "Intuit charge" },
};

const STATE_META = {
  fired: { cls: "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800", label: "Would have fired", Icon: CheckCircle2 },
  pending: { cls: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700", label: "Pending next step", Icon: Clock },
  skipped: { cls: "bg-slate-50 text-slate-400 border-slate-200 line-through dark:bg-slate-900 dark:text-slate-500 dark:border-slate-800", label: "Won't fire", Icon: Ban },
};

export default function SandboxEventsDrawer({ orderId, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    if (!open || !orderId) return;
    let cancelled = false;
    setLoading(true);
    setPayload(null);
    API.get(`/sandbox/orders/${orderId}/events-preview`)
      .then(({ data }) => { if (!cancelled) setPayload(data); })
      .catch((e) => { if (!cancelled) setPayload({ error: e?.response?.data?.detail || "Failed to load events" }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, orderId]);

  const order = payload?.order || {};
  const tiles = payload?.lifecycle_preview || [];
  const recorded = payload?.recorded_events || [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="sandbox-events-drawer">
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-orange-500" />
            What would have fired
          </SheetTitle>
          <SheetDescription asChild>
            <div>
              {order.scenario_label && (
                <>
                  <p className="font-medium text-slate-700 dark:text-slate-200 mt-1">{order.scenario_label}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {order.medication_name} · {(order.payment_type || "").replace("_", " ")} · {order.patient_name} · {order.patient_state}
                  </p>
                </>
              )}
              <p className="text-[11px] text-slate-400 mt-2">
                All data is sandbox-synthetic. No real patient receives any communication from these tiles.
              </p>
            </div>
          </SheetDescription>
        </SheetHeader>

        <div className="mt-5 space-y-5">
          {loading && (
            <div className="text-center py-10 text-slate-400">
              <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2" />
              Loading events…
            </div>
          )}

          {!loading && payload?.error && (
            <div className="text-sm text-rose-500 border border-rose-200 rounded-lg p-3">{payload.error}</div>
          )}

          {!loading && !payload?.error && (
            <>
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  Lifecycle preview
                </h3>
                <div className="space-y-2">
                  {tiles.map((t, i) => {
                    const km = KIND_META[t.kind] || KIND_META.email;
                    const sm = STATE_META[t.state] || STATE_META.pending;
                    const KI = km.Icon;
                    const SI = sm.Icon;
                    return (
                      <div
                        key={`${t.stage}-${t.kind}-${i}`}
                        className={`border rounded-lg p-3 flex items-start gap-3 ${t.state === "skipped" ? "opacity-60" : ""}`}
                        data-testid={`sandbox-event-tile-${t.stage}-${t.kind}`}
                      >
                        <KI className={`w-4 h-4 mt-0.5 flex-shrink-0 ${km.color}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{t.label}</p>
                            <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{km.label}</Badge>
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t.description}</p>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border inline-flex items-center gap-1 whitespace-nowrap ${sm.cls}`}>
                          <SI className="w-2.5 h-2.5" />
                          {sm.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </section>

              {recorded.length > 0 && (
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                    Audit trail · {recorded.length} stubbed call{recorded.length === 1 ? "" : "s"}
                  </h3>
                  <div className="space-y-2">
                    {recorded.map((e, i) => (
                      <div key={i} className="border rounded-lg p-3 bg-slate-50 dark:bg-slate-800/40" data-testid={`sandbox-audit-row-${i}`}>
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <p className="text-xs font-mono text-slate-700 dark:text-slate-200">{e.kind}</p>
                          <p className="text-[10px] text-slate-400">{e.at ? new Date(e.at).toLocaleString() : ""}</p>
                        </div>
                        {e.detail && Object.keys(e.detail).length > 0 && (
                          <pre className="text-[10px] mt-1 text-slate-500 dark:text-slate-400 whitespace-pre-wrap break-all">
{JSON.stringify(e.detail, null, 2)}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
